import { CONSENT_GRANT_DURATION_MS, CONSENT_REFUSAL_DURATION_MS } from '../../lib/consent/contract'
import { parseConsentState } from '../../lib/consent/parseConsentState'
import { deleteCookie, extractCookie, setCookie } from 'lib/web/cookieUtils'
import { CONSENT_COOKIE_NAME } from './types'
import { notifyConsentChanged } from './changeEvents'
import type { ConsentPurpose, ConsentState, ConsentStatus } from './types'

let pageConsent: ConsentState | undefined

function readSavedConsentState(): ConsentState | undefined {
  try {
    return parseConsentState(extractCookie(document.cookie, CONSENT_COOKIE_NAME))
  } catch {
    // Unreadable cookies are not consent. Keep optional integrations disabled.
    return undefined
  }
}

export function readConsentState(): ConsentState | undefined {
  return readSavedConsentState() ?? pageConsent
}

export function continueWithoutOptionalCookies() {
  pageConsent = {
    version: 1,
    status: 'necessary_only',
    purposes: [],
    decidedAt: Date.now(),
    expiresAt: Date.now() + CONSENT_REFUSAL_DURATION_MS,
  }
  notifyConsentChanged()
}

export function writeConsentState(status: ConsentStatus, purposes: Array<ConsentPurpose>): ConsentState | undefined {
  const decidedAt = Date.now()
  const state: ConsentState = {
    version: 1,
    status,
    purposes,
    decidedAt,
    expiresAt: decidedAt + (status === 'accepted' ? CONSENT_GRANT_DURATION_MS : CONSENT_REFUSAL_DURATION_MS),
  }
  // Host-only cookie (no Domain=). A Domain= cookie is a different cookie than
  // the host-only le_consent the server and Playwright read, and it would also
  // leak the choice to sibling subdomains.
  try {
    setCookie(CONSENT_COOKIE_NAME, JSON.stringify(state), Math.ceil((state.expiresAt - decidedAt) / 1000), '/', 'Lax')
    // Some browsers silently reject writes. Do not report success or reload
    // into the same undecided page (or a previously saved grant).
    if (JSON.stringify(readSavedConsentState()) !== JSON.stringify(state)) return undefined
  } catch {
    return undefined
  }
  pageConsent = undefined
  notifyConsentChanged()
  return state
}

export function clearConsentState(): void {
  pageConsent = undefined
  deleteCookie(CONSENT_COOKIE_NAME)
}
